import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useDashboard } from "../context/DashboardContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { formatCompactINR, timeAgo } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Building2, Plus, RefreshCw, Trash2, Pencil, KeyRound, MapPin } from "lucide-react";

const EMPTY = {
  name: "", city: "", monthly_budget: "",
  meta: { ad_account_id: "", access_token: "" },
  google: { developer_token: "", client_id: "", client_secret: "", refresh_token: "", customer_id: "", login_customer_id: "" },
};

function SourceBadge({ source }) {
  const live = source === "live";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${live ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
      {live ? "Live" : "Mock"}
    </span>
  );
}

export default function Branches() {
  const { loadBranches, refreshData } = useDashboard();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/branches");
    setBranches(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name, city: b.city, monthly_budget: b.monthly_budget,
      meta: { ad_account_id: b.meta?.ad_account_id || "", access_token: "" },
      google: { developer_token: "", client_id: b.google?.client_id || "", client_secret: "",
                refresh_token: "", customer_id: b.google?.customer_id || "", login_customer_id: b.google?.login_customer_id || "" },
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Branch name required");
    setBusy(true);
    try {
      const payload = {
        name: form.name, city: form.city, monthly_budget: Number(form.monthly_budget || 0),
        meta: form.meta, google: form.google,
      };
      if (editing) await api.put(`/branches/${editing.id}`, payload);
      else await api.post("/branches", payload);
      toast.success(editing ? "Branch updated & synced" : "Branch created & synced");
      setOpen(false);
      await load(); await loadBranches(); refreshData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const syncBranch = async (id) => {
    setSyncingId(id);
    try {
      await api.post(`/branches/${id}/sync`);
      toast.success("Branch synced");
      await load(); refreshData();
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this branch and all its data?")) return;
    try { await api.delete(`/branches/${id}`); toast.success("Branch deleted"); await load(); await loadBranches(); refreshData(); }
    catch (e) { toast.error("Failed"); }
  };

  const inp = "bg-[#0F172A] border-white/10 text-white h-10 focus-visible:ring-[#38BDF8]";

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Branches"
        subtitle="Manage clinic branches and their Meta & Google ad accounts"
        action={isAdmin && (
          <Button data-testid="add-branch-btn" onClick={openCreate}
            className="h-9 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full text-xs">
            <Plus className="w-4 h-4 mr-1.5" /> Add Branch
          </Button>
        )} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {branches.map((b) => (
          <div key={b.id} data-testid={`branch-card-${b.id}`} className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#38BDF8]/15 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#38BDF8]" />
                </div>
                <div>
                  <p className="font-display font-semibold text-white">{b.name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{b.city || "—"}</p>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button data-testid={`edit-branch-${b.id}`} onClick={() => openEdit(b)} className="h-8 w-8 p-0 bg-[#0F172A] hover:bg-[#334155] border border-white/10 text-slate-300 rounded-lg"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`delete-branch-${b.id}`} onClick={() => remove(b.id)} className="h-8 w-8 p-0 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 text-[#EF4444] rounded-lg"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-400">Monthly Budget</span>
              <span className="font-display text-lg font-bold ml-auto">{formatCompactINR(b.monthly_budget)}</span>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Meta Ads</span>
                <div className="flex items-center gap-2"><SourceBadge source={b.meta_source} /><span className="text-xs text-slate-500">{timeAgo(b.meta_last_synced)}</span></div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Google Ads</span>
                <div className="flex items-center gap-2"><SourceBadge source={b.google_source} /><span className="text-xs text-slate-500">{timeAgo(b.google_last_synced)}</span></div>
              </div>
            </div>

            <Button data-testid={`sync-branch-${b.id}`} onClick={() => syncBranch(b.id)} disabled={syncingId === b.id}
              className="w-full mt-4 h-9 bg-[#0F172A] hover:bg-[#334155] border border-white/10 text-slate-200 rounded-full text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncingId === b.id ? "animate-spin" : ""}`} /> Sync now
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Branch" : "New Branch"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
                <Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">City</Label>
                <Input data-testid="branch-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Monthly Budget (₹)</Label>
              <Input data-testid="branch-budget-input" type="number" value={form.monthly_budget} onChange={(e) => setForm({ ...form, monthly_budget: e.target.value })} className={inp} /></div>

            <Tabs defaultValue="meta">
              <TabsList className="bg-[#1E293B] border border-white/10 w-full">
                <TabsTrigger value="meta" className="flex-1 data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Meta Keys</TabsTrigger>
                <TabsTrigger value="google" className="flex-1 data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Google Keys</TabsTrigger>
              </TabsList>
              <TabsContent value="meta" className="space-y-3 mt-4">
                <p className="text-xs text-slate-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" />Leave blank to keep demo data. Paste to go live.</p>
                <Input data-testid="meta-account-input" placeholder="Ad Account ID (act_XXXX)" value={form.meta.ad_account_id}
                  onChange={(e) => setForm({ ...form, meta: { ...form.meta, ad_account_id: e.target.value } })} className={inp} />
                <Input data-testid="meta-token-input" type="password" placeholder="Long-lived Access Token" value={form.meta.access_token}
                  onChange={(e) => setForm({ ...form, meta: { ...form.meta, access_token: e.target.value } })} className={inp} />
              </TabsContent>
              <TabsContent value="google" className="space-y-3 mt-4">
                <p className="text-xs text-slate-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" />All fields required for live Google Ads sync.</p>
                <Input data-testid="google-dev-token" type="password" placeholder="Developer Token" value={form.google.developer_token}
                  onChange={(e) => setForm({ ...form, google: { ...form.google, developer_token: e.target.value } })} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Client ID" value={form.google.client_id} onChange={(e) => setForm({ ...form, google: { ...form.google, client_id: e.target.value } })} className={inp} />
                  <Input type="password" placeholder="Client Secret" value={form.google.client_secret} onChange={(e) => setForm({ ...form, google: { ...form.google, client_secret: e.target.value } })} className={inp} />
                </div>
                <Input type="password" placeholder="Refresh Token" value={form.google.refresh_token} onChange={(e) => setForm({ ...form, google: { ...form.google, refresh_token: e.target.value } })} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Customer ID" value={form.google.customer_id} onChange={(e) => setForm({ ...form, google: { ...form.google, customer_id: e.target.value } })} className={inp} />
                  <Input placeholder="Login/MCC ID (opt)" value={form.google.login_customer_id} onChange={(e) => setForm({ ...form, google: { ...form.google, login_customer_id: e.target.value } })} className={inp} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button data-testid="save-branch-btn" onClick={save} disabled={busy}
              className="w-full h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full">
              {busy ? "Saving & syncing…" : editing ? "Save & Sync" : "Create & Sync"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
